/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, ViewChild, ElementRef, Output, EventEmitter, Input } from '@angular/core';

import { ImageService } from "../../../services/image.service";

import { BaseEntity } from "../../../models/base-entity";
import { BaseInstance } from '../../../models/base-instance';
import { EmGroup } from '../../../models/em-group';
import { GroupType } from '../../../models/group-type';
import { InstanceData } from "../../../models/instance-data";
import { LocationInstance } from "../../../models/location-instance";
import { ObjectCategory } from "../../../models/object-category";
import { ReaderInstance } from "../../../models/reader-instance";
import { TagInstance } from "../../../models/tag-instance";
import { TagUI } from "../../../models/tag-ui";
import { FunctionExecutingDialogComponent } from "./function-executing-dialog.component";
import { DropDownItem } from "../../../models/dropdown-item";
import { CanActivateAuthGuard } from "../../../can-activate.authguard";
import { IconUploadConfirmDialogService } from 'app/services/icon-upload-confirm-dialog-service.service';
import { PropertyDataUI } from './property-data-ui';
import { PropertyTypeUtil } from 'app/models/property-type';
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from 'app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component';
import { Function } from "app/models/function";
declare var $: any;


@Component({
  selector: 'app-entity-instance-dialog',
  templateUrl: './entity-instance-dialog.component.html'
})
export class EntityInstanceDialogComponent implements OnInit {

  @ViewChild('instanceImage')
  mEntityInstanceImageFileInput: ElementRef;

  @ViewChild('instanceIcon')
  mEntityInstanceIconFileInput: ElementRef;

  @ViewChild('functionExecutingDialog')
  mFunctionExecutingDialog: FunctionExecutingDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;


  /*
   * Inputs used by all Entity Instances
   */
  @Input()  //Entity Instance being created/edited
  instance: BaseInstance;

  @Input()  //Name of the Entity Instance being edited.  Displayed in the Dialog Title when editing an Entity Instance
  instanceName: string;

  @Input()  //Entity Type the Entity Instance is a type of.  Modeled in the UI combobox/select as the selected 'Type'
  instanceEntityType: BaseEntity;

  @Input()  //Type of Instance that is being created.  Used to figure out what fields should be shown to the User
  instanceType: GroupType;

  @Input()  //Name of the Entity Type being created/updated.  Displayed in the Dialog Header
  instanceTypeName: string;

  @Input()  //Available Entity Types for the Instance.  Populates the UI combobox/select.
  availableEntityTypes: BaseEntity[];


  /*
   * Outputs used by all Entity Instances
   */
  @Output() //Event/Method called when the User clicks the CREATE or UPDATE button indicating they want to attempt to save
  createOrUpdateEvent = new EventEmitter<boolean>();

  @Output() //Event/Method called when the User clicks a Group button.  Used to tell the infrastructure page to display the Group Dialog for the passed Group.
  showGroup = new EventEmitter<EmGroup>();

  @Output() //Event/Method called when the User selects an Entity Type for the Instance.  Handled by the individual pages.
  selectedEntityTypeChangedEvent = new EventEmitter<BaseEntity>();

  @Output()
  refreshInstanceDataEvent = new EventEmitter<boolean>();


  /*
   * Object Specific Inputs.  Used by sub categories of the Object Instances (Devices/Assets).  Let's the UI know what category of Object Instances is being created/edited so it can configure what to show/display to the User.
   */
  @Input()
  objectCategory: ObjectCategory;


  /*
   * Location Specific Inputs used by Reader/Location/Zone/Antenna Instances.  Allows the setting of a Location Instance value
   */
  @Input()
  availableLocations: LocationInstance[];

  @Input()
  selectedLocation: LocationInstance;

  /*
   * Location Specific Outputs used by Reader/Location/Zone/Antenna Instances.  Called when the selected Location Instance changes
   */
  @Output()
  selectedLocationChangedEvent = new EventEmitter<LocationInstance>();


  /*
   * Reader Specific Inputs, used by Antenna Instances.  Allows the setting of a Reader Instance value
   */
  @Input()
  availableReaders: ReaderInstance[];

  @Input()
  selectedReader: ReaderInstance;

  /*
   * Reader Specific Outputs used by Antenna Instances.  Called when the selected Reader Instance changes
   */
  @Output()
  selectedReaderChangedEvent = new EventEmitter<ReaderInstance>();


  /** Original Instance Data for the Instance being edited / created */
  originalInstanceData: InstanceData[] = [];

  /** Property Data UIs used to display the Data for the Instance being edited */
  propertyDataUis: PropertyDataUI[] = [];


  // Variables for the multi-selection tag selector
  multiSelectTagDropDownList: DropDownItem[] = []; // All the tags to show in the selector
  multiSelectTagSelectedItems: DropDownItem[] = []; // Just the tags that are assigned to this instance

  multiSelectTagDropDownSettings = {
    singleSelection: false,
    text: "Tags:",
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    enableSearchFilter: true,
    classes:'myclass custom-class',
    maxHeight: 150,
    noDataLabel:'No emAssets chosen',
    position:"top"
  };

  /*
   * Tag Specific Inputs, used by Asset Instances.  Allows the setting of a Tag Instance value
   */

  mAvailableTags: TagUI[] = [];
  mSelectedTags: TagInstance[] = [];

  @Input()
  set availableTags(availableTagInstances: TagInstance[]) {

    // Create the tag drop down list to be used in the component
    this.multiSelectTagDropDownList = [];
    this.mAvailableTags = [];

    // Add all the emAssets assigned to this user
    if (availableTagInstances && availableTagInstances.length > 0) {
      for (const tag of availableTagInstances) {
        if (tag) {
          this.multiSelectTagDropDownList.push(new DropDownItem(tag.id, tag.name));

          const tagUI = new TagUI();
          tagUI.tag = tag;
          for(const selectedTag of this.multiSelectTagSelectedItems) {
            if (selectedTag.id == tag.id) {
              tagUI.selected = true;
            }
          }

          this.mAvailableTags.push(tagUI);

          console.log("entity-instance dialog component availableTags: added tag " + tag.name);
        }
      }
    }
  }

  @Input()
  set selectedTags(selectedTagInstances: TagInstance[]) {
    this.multiSelectTagSelectedItems = [];

    console.log("selectedTags called");
    if (selectedTagInstances) {
      console.log("selectedTagInstances Length = " + selectedTagInstances.length);
    } else {
      console.log("selectedTagInstances Length = 0");

    }
    // add the selected tag to the multiSelectTagDropDownList
    if (selectedTagInstances && selectedTagInstances.length > 0) {
      for (const tag of selectedTagInstances) {
        if (tag) {
          console.log("Adding selected tag to multiSelectTagsSelectedItems");
          this.multiSelectTagSelectedItems.push(new DropDownItem(tag.id, tag.name));

          for(const availTag of this.mAvailableTags) {
            if (availTag.tag.id == tag.id) {
              availTag.selected = true;
            }
          }
          console.log("Adding tag " + tag.name + " to selected tags");
        }
      }
    }

  }

  /*
   * Tag Specific Outputs, used by Asset Instances.  Called when the selected Tag Instance changes
   */
  @Output()
  selectedTagChangedEvent = new EventEmitter<TagInstance[]>();

  constructor(
    private mImageService: ImageService,
    private mIconUploadConfirmDialogService: IconUploadConfirmDialogService
  ) { }

  ngOnInit(): void {

  }

  public showDialog(): void {
    CanActivateAuthGuard.popupActive = true;

    //Reset the active Tab to the "information" Tab
    $('.nav-tabs a[href="#antennaInstanceInformation"]').tab('show');

    //Show the Dialog
    $("#updateAntennaInstanceDiv").modal("show");
  }

  public hideDialog(): void {
    CanActivateAuthGuard.popupActive = false;

    //Hide the Dialog and reset the User being edited/created...
    $("#updateAntennaInstanceDiv").modal("hide");
  }


  public imageSource(): string {
    return this.mImageService.getImageSourceForInstance(this.instance);
  }

  public iconSource(): string {
    return this.mImageService.getIconSourceForInstance(this.instance);
  }


  isEditDialog(): boolean {
    return this.instance != null && this.instance.id != null;
  }

  isNewDialog(): boolean {
    return this.instance != null && this.instance.id == null;
  }

  isEntityType_AntennaInstance(): boolean {
    return this.instanceType != null && this.instanceType == GroupType.ANTENNAINSTANCE;
  }

  isEntityType_DriverObjectInstance(): boolean {
    return this.instanceType != null && this.instanceType == GroupType.DRIVEROBJECTINSTANCE;
  }

  isEntityType_LocationInstance(): boolean {
    return this.instanceType != null && this.instanceType == GroupType.LOCATIONINSTANCE;
  }

  isEntityType_ObjectInstance(): boolean {
    return this.instanceType != null && this.instanceType == GroupType.OBJECTINSTANCE;
  }

  isEntityType_ReaderInstance(): boolean {
    return this.instanceType != null && this.instanceType == GroupType.READERINSTANCE;
  }

  isEntityType_ServerObjectInstance(): boolean {
    return this.instanceType != null && this.instanceType == GroupType.SERVEROBJECTINSTANCE;
  }

  isEntityType_ServiceInstance(): boolean {
    return this.instanceType != null && this.instanceType == GroupType.SERVICEINSTANCE;
  }

  isEntityType_TagInstance(): boolean {
    return this.instanceType != null && this.instanceType == GroupType.TAG;
  }

  isEntityType_ZoneInstance(): boolean {
    return this.instanceType != null && this.instanceType == GroupType.READZONEINSTANCE;
  }

  isEntitySubType_Asset(): boolean {
    return this.isEntityType_ObjectInstance() && this.objectCategory != null && this.objectCategory == ObjectCategory.ASSET;
  }

  isEntitySubType_People(): boolean {
    return this.isEntityType_ObjectInstance() && this.objectCategory != null && this.objectCategory == ObjectCategory.PEOPLE;
  }

  isEntitySubType_Device(): boolean {
    return this.isEntityType_ObjectInstance() && this.objectCategory != null && this.objectCategory == ObjectCategory.DEVICE;
  }

  changeImageClicked(): void {
    this.mEntityInstanceImageFileInput.nativeElement.click();
  }

  onImageFileChanged(event: any): void {
    let selectedFile: File = null;
    if (event.target.files.length > 0) {
      selectedFile = event.target.files[0];
    }

    if (selectedFile != null) {
      //Upload / Create the Image and set it to the Instance
      this.submitImage(selectedFile);

      //Reset the Image File <input> Element's value back to null to clear the selected File
      this.mEntityInstanceImageFileInput.nativeElement.value = null;
    }
  }

  resetImage(): void {
    if (this.instance != null) {
      this.instance.image = null;
    }
  }

  private async submitImage(file: File): Promise<void> {
    try {
      //Call the Service to Upload / Create the Image for the given File
      const savedImage = await this.mImageService.uploadImageFile(file);

      //Update the Image for the Instance being edited to the one that was just created
      if (this.instance != null) {
        this.instance.image = savedImage;
      }
    } catch (error) {
      //If an error occurs, log it and display it to the User
      this.handleError(error);
    }
  }

  changeIconClicked(): void {
    this.mEntityInstanceIconFileInput.nativeElement.click();
  }

  onIconFileChanged(event: any): void {
    if (event.target.files.length === 1) {
      this.submitIcon(event.target.files[0]);
    }
  }

  resetIcon(): void {
    if (this.instance != null) {
      this.instance.icon = null;
    }
  }

  submitIcon(file: any): void {
    //KHT 08/24/2021 - added IconUploadConfirmDialog to confirm icon resize on upload
    this.mIconUploadConfirmDialogService.show().then((confirmed) => {
      if (confirmed) {
        console.log("Icon Upload Proceeding...");
        const formData = new FormData();
        formData.append("file", file);
        formData.append("isIcon", "true");//KHT 08/24/2021 - backend checks for 'isIcon' in the formData, and resizes the icon if it is too large
        this.mImageService
          .uploadImage(formData)
          .then(
            icon => {
              if (this.instance != null) {
                this.instance.icon = icon;
              }
              this.mEntityInstanceIconFileInput.nativeElement.value = "";
            },
            error => {
              this.mEntityInstanceIconFileInput.nativeElement.value = "";
              this.handleError(error);
            }
          );
      }
      else {
        console.log("Icon Upload Halted!");
      }
    }).catch(() => {
      console.log('User dismissed the dialog (e.g., by using ESC, clicking the cross icon, or clicking outside the dialog)')
    });

  }

  selectedEntityTypeChanged(): void {
    this.selectedEntityTypeChangedEvent.emit(this.instanceEntityType);
  }

  refreshInstanceDataClicked(): void {
    this.refreshInstanceDataEvent.emit(true);
  }

  createOrUpdateClicked(): void {
    /*
     * Creating / Updating the Instance... Need to set it's 
     * "data" property from all the "PropertyDataUis" currently being
     * used to display / edit it.  
     * 
     * This also fixes the "object_value" property of all the "InstanceData"
     * being added / sent for the create / update to prevent issues in the 
     * backend.
     */
    const data: InstanceData[] = [];
    if (this.propertyDataUis != null) {
      const tempData: InstanceData[] = [];
      for (const propertyDataUis of this.propertyDataUis) {
        tempData.push(...propertyDataUis.mData);
      }
      data.push(...(JSON.parse(JSON.stringify(tempData)) as InstanceData[]));
      if (data.length > 0) {
        data.forEach(datum => PropertyTypeUtil.fixOutgoingInstanceDataObjectValueForType(datum.property.type, datum));
      }
    }
    this.instance.data = data;

    this.createOrUpdateEvent.emit(true);
  }

  showGroupClicked(group: EmGroup): void {
    this.showGroup.emit(group);
  }

  selectedLocationChanged(): void {
    this.selectedLocationChangedEvent.emit(this.selectedLocation);
  }

  selectedReaderChanged(): void {
    this.selectedReaderChangedEvent.emit(this.selectedReader);
  }

  functionStartingToExecute(func: Function): void {
    this.mFunctionExecutingDialog.showDialogStartExecutingFunction(func);
  }

  functionFinishedExecutingSuccessfully(response: any): void {
  this.mFunctionExecutingDialog.showFunctionExecutionSuccess(response);
}

  functionFinishedExecutingWithError(error: string): void {
    this.mFunctionExecutingDialog.showFunctionExecutionError(error);
  }

  setTagsToAvailableTagsSelected(): void {
    this.mSelectedTags = [];
    if (this.mAvailableTags.length > 0) {
      for (const availableTag of this.mAvailableTags) {
        if (availableTag.selected) {
          console.log("added selected tag " + availableTag.selected);
          this.mSelectedTags.push(availableTag.tag);
        }
      }
      this.selectedTagChangedEvent.emit(this.mSelectedTags);
    }
  }

  selectTagItem(event: any): void {
    console.log("Adding Tag item: " + event.id);
    for(const tagUI of this.mAvailableTags) {
      if (tagUI.tag.id == event.id) {
        console.log("add selected to " + tagUI.tag.id);
        tagUI.selected = true;
      }
    }
    this.setTagsToAvailableTagsSelected();

  }

  deselectTagItem(event: any): void {

    for(const tagUI of this.mAvailableTags) {
      if (tagUI.tag.id == event.id) {
        tagUI.selected = false;
      }
    }
    this.setTagsToAvailableTagsSelected();


  }

  selectAllTagItems(): void {
    console.log("Selecting all tag items");
    for(const dropDownItem of this.multiSelectTagDropDownList) {
      this.multiSelectTagSelectedItems.push(dropDownItem);
    }
    for(const tagUI of this.mAvailableTags) {
      tagUI.selected = true;
    }
    this.setTagsToAvailableTagsSelected();
  }

  deselectAllTagItems(): void {
    this.multiSelectTagSelectedItems = [];
    for(const tagUI of this.mAvailableTags) {
      tagUI.selected = false;
    }
    this.setTagsToAvailableTagsSelected();
  }

  handleError(error: any): void {
    console.error("EntityInstanceDialogComponent.handleError() - Error: " + error);
    if (error != null) {
      //alert(response.text());
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }
  }

  /**
   * Sets the original "InstanceData[]" of the data for the instance being
   * created / edited.  This is used when "refreshData()" is called so 
   * that it created the PropertyDataUis with all the current values
   * for the Instance.
   */
  public async setOriginalInstanceData(originalInstanceData: InstanceData[]): Promise<void> {
    if (originalInstanceData == null) {
      this.originalInstanceData = [];
    } else {
      this.originalInstanceData = JSON.parse(JSON.stringify(originalInstanceData)) as InstanceData[];
    }
  }

  /**
   * Creates / Recreates the "PropertyDataUis" that will be added
   * to the "propertyDataUis" variable as the Property Data being 
   * displayed / edited.  This uses the "instanceEntityType" variable
   * to obtain the Properties to be displayed and the "originalInstanceData" 
   * variable for any existing data to be added / set for that Property.
   */
  public async refreshData(): Promise<void> {
    // Holds the "PropertyDataUi"s being created for the Property / Data values
    const propertyDataUis: PropertyDataUI[] = [];

    // If there is an "instanceEntityType" and it has Properties...
    if (this.instanceEntityType?.properties?.length > 0) {
      // Create a copy of the original InstanceData[] as it will be massaged for the UI use
      const copiedOriginalData = this.originalInstanceData == null? null : JSON.parse(JSON.stringify(this.originalInstanceData)) as InstanceData[];
      /*
       * Cycle through the Properties for the Entity Type, creating a 
       * new "PropertyDataUi" for each one and adding any "InstanceData"
       * records from the original instance data if it was defined
       * for that property.  If no Data is defined for the Property,
       * one is added / created.
       */
      for (const property of this.instanceEntityType.properties) {
        const newPropertyDataUi = new PropertyDataUI();
        newPropertyDataUi.mData = [];
        newPropertyDataUi.mProperty = property;
        for (const datum of copiedOriginalData) {
          if (datum?.property?.id == property?.id) {
            datum.property = property;
            // Fix the "object_data" value of the incoming "InstanceData" as the JSON is structured weird coming from the backend.
            PropertyTypeUtil.fixIncomingInstanceDataObjectValueForType(property.type, datum);
            newPropertyDataUi.mData.push(datum);
          }
        }
        if (newPropertyDataUi.mData.length == 0) {
          const datum = new InstanceData();
          datum.property = property;
          newPropertyDataUi.mData.push(datum);
        }
        propertyDataUis.push(newPropertyDataUi);
      }
    }

    // Clear / Populate the "propertyDataUis" variable with the new "PropertyDataUi"s created above
    while(this.propertyDataUis.length > 0) {
      this.propertyDataUis.pop();
    }
    this.propertyDataUis.push(...propertyDataUis);
  }

}


